import React from 'react';
import { ROUTES, OTHER_ROUTES } from './routes';

export type EnumLiteralsOf<T extends object> = T[keyof T];

export type GroupType = 'store' | 'item';
export type TableTypes = 'item' | 'account';

export const CONTEXT = 'signals-v2';
export const CONTEXT_STORE_GROUP = 'signals-v2-store-group';
export const CONTEXT_ITEM_GROUP = 'signals-v2-item-group';
export const DEFAULT_PER_PAGE = 15;
export const ACCOUNTS_TYPE = 'Accounts';
export const STORES_TYPE = 'Stores';
export const STORES_GROUPS_TYPE = 'Store Groups';
export const ITEMS_TYPE = 'Items';
export const ITEMS_GROUPS_TYPE = 'Item Groups';
export const CAMPAIGNS_TYPE = 'Campaigns';
export const CAMPAIGNS_ARCHIVED_TYPE = 'Archived';
export const CAMPAIGNS_ACTIVE_TYPE = 'Active';
export const CAMPAIGNS_INACTIVE_TYPE = 'Inactive';
export const BRANDS_TYPE = 'Brands';
export const DEFAULT_FILTER_ORDER = ['accounts', 'states', 'stores', 'items'];
export const RETRYABLE_ERROR_TYPES = ['Http:ExecutionTimeoutException', 'ExecutionTimeout'];

export const TrendSalesType = 'Dollars';
export const TrendLocationsType = 'Locations';
export const TrendUnitsType = 'Units';
export const TrendChartTypes = [TrendSalesType, TrendLocationsType, TrendUnitsType];
export const ShareAccountSalesType = 'Account Dollars';
export const ShareAccountUnitType = 'Account Units';
export const ShareItemSalesType = 'Item Dollars';
export const ShareItemUnitType = 'Item Unit';
export const ShareOpportunitySalesType = 'Opportunity Dollars';
export const ShareOpportunityVoidsType = 'Opportunity Voids';
export const ShareChartTypes = [
  ShareAccountSalesType,
  ShareAccountUnitType,
  ShareItemSalesType,
  ShareItemUnitType
];
export const ItemShareChartTypes = [
  ShareItemSalesType,
  ShareItemUnitType,
  ShareAccountSalesType,
  ShareAccountUnitType
];
export const DistributionGainsType = 'Distribution Gains';
export const DistributionLossesType = 'Distribution Losses';
export const DistributionChartTypes = [DistributionGainsType, DistributionLossesType];

export type BrandType = {
  id: string;
  name: string;
};

export const CAMPAIGNS_ITEMS_TYPES = Object.freeze({
  ACCOUNTS: 'accounts',
  ITEMS: 'items',
  STATES: 'states',
  STORES: 'stores'
});

export const CampaignObjectiveKey = Object.freeze({
  AUDIT: 'audit',
  IN_STORE_DEMO: 'inStoreDemo',
  IN_STORE_PROMOTION: 'inStorePromotion',
  NEW_PRODUCT_INTRO: 'newProductIntro',
  PLACEMENT: 'placement',
  PRICING_COMPLIANCE: 'pricingCompliance'
});

export const CampaignObjectiveKeyLabel = Object.freeze({
  [CampaignObjectiveKey.AUDIT]: 'Audit',
  [CampaignObjectiveKey.IN_STORE_DEMO]: 'In-Store Demo',
  [CampaignObjectiveKey.IN_STORE_PROMOTION]: 'In-Store Promotion',
  [CampaignObjectiveKey.NEW_PRODUCT_INTRO]: 'New Product Intro',
  [CampaignObjectiveKey.PLACEMENT]: 'Placement',
  [CampaignObjectiveKey.PRICING_COMPLIANCE]: 'Pricing Compliance'
});

export type CampaignObjectiveKeyType = EnumLiteralsOf<typeof CampaignObjectiveKey>;

export type FilterSelectionType = {
  accounts: Array<string>;
  items: Array<string>;
  states: Array<string>;
  stores: Array<string>;
};

export const GlobalFilterKey = Object.freeze({
  GLOBAL: 'global',
  DETAILS: 'details'
});

export type GlobalFilterKeyType = EnumLiteralsOf<typeof GlobalFilterKey>;

export type GlobalFilterType = FilterType & {
  endWeek: string;
  weeks: number;
  brand: string;
};

export type SelectionType = 'accounts' | 'items' | 'states' | 'stores';

export type FilterType = { [key in SelectionType]: Array<string> };

export type PageableType = {
  page: number;
  pageSize: number;
  sortedBy: string;
  sortDirection: string;
};

export type PeriodEndWeekType = {
  spins: string;
  nielsen: string;
};

export const SortDirection = {
  ASC: 'ASC',
  DESC: 'DESC'
};

export type HamburgerMenuType = {
  icon?: string;
  name: string;
  selected?: boolean;
  url?: string;
};

export type HeaderType = {
  content?: React.ReactElement;
  label: string;
  sortBy?: string;
  default?: boolean;
};

export type UserInfoType = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export const INITIAL_BREADCRUMB = new Map([
  [ROUTES.OVERVIEW, 'Brand Overview'],
  [ROUTES.VOIDS, 'Void Overview'],
  [ROUTES.ACCOUNTS, 'Account Overview'],
  [ROUTES.ITEMS, 'Item Overview'],
  [OTHER_ROUTES.CAMPAIGN_DETAILS, 'Campaign Overview'],
  [OTHER_ROUTES.GROUP_DETAILS, 'Group Overview'],
  [ROUTES.PORTFOLIO, 'Portfolio Overview']
]);

export const TAB_TO_QUERY_PARAM = new Map([
  [ACCOUNTS_TYPE, 'account'],
  [ITEMS_TYPE, 'item'],
  [STORES_TYPE, 'store'],
  [STORES_GROUPS_TYPE, 'stores_group'],
  [ITEMS_GROUPS_TYPE, 'items_group'],
  [CAMPAIGNS_TYPE, 'campaign'],
  [CAMPAIGNS_ARCHIVED_TYPE, 'archived_campaign']
]);

export const QUERY_PARAM_TO_TAB = new Map([
  ['account', ACCOUNTS_TYPE],
  ['item', ITEMS_TYPE],
  ['store', STORES_TYPE],
  ['stores_group', STORES_GROUPS_TYPE],
  ['items_group', ITEMS_GROUPS_TYPE],
  ['campaign', CAMPAIGNS_TYPE],
  ['archived_campaign', CAMPAIGNS_ARCHIVED_TYPE]
]);

export const SELECTION_TO_TYPE = new Map([
  ['pdf', 'PDF'],
  ['xls', 'XLSX']
]);
export const STATUS_TO_TYPE = new Map([
  ['active', 'active'],
  ['inactive', 'inactive'],
  ['archived', 'archived']
]);

export type StoreTags = {
  spins: string;
  nielsen: string;
};

export type itemFilters = {
  items?: Array<string>;
  counts?: {
    items?: number;
  };
};

export type storeFilters = {
  accounts?: Array<string>;
  states?: Array<string>;
  stores?: Array<string>;
  counts?: {
    accounts?: number;
    states?: number;
    stores?: number;
  };
};

export type campaignsDataType = {
  id: string;
  name: string;
  itemGroupName: string;
  storeGroupName: string;
  objective: string;
  notes?: string;
  startDate: string;
  endDate?: string;
  itemFilters?: itemFilters;
  storeFilters?: storeFilters;
};

export type StoreDataType = {
  address: string;
  city: string;
  name: string;
  nielsenTag: string;
  postalCode: string;
  spinsTag: string;
  state: string;
  storeTag: string;
};

export type ProductDataType = {
  name: string;
  upc: string;
};
